import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/generals/section-heading'
import { StatusLabel } from '@/components/generals/status-label'
import { ConceptScreen } from '@/components/landing/concept-screen'
import { TabletFrame } from '@/components/landing/tablet-frame'
import { businessIcons as iconMap } from '@/components/landing/business-icons'

import { businesses } from '@/constants/content'

const BusinessesSection: FC = (): ReactNode => {
    const [selectedId, setSelectedId] = useState(businesses.items[0].id)
    const selected = businesses.items.find(item => item.id === selectedId) ?? businesses.items[0]
    const SelectedIcon = iconMap[selected.icon]

    return (
        <section id='businesses' aria-labelledby='businesses-heading' className='relative py-24 sm:py-32'>
            <div className='container-site'>
                <SectionHeading
                    id='businesses-heading'
                    eyebrow={businesses.eyebrow}
                    title={businesses.heading}
                    intro={businesses.intro}
                />

                <p className='mt-6 inline-flex items-center gap-2 rounded-lg border border-soft-white/10 bg-navy-900/60 px-3 py-2 text-xs text-cool-grey-light'>
                    {businesses.label}
                </p>

                <div className='mt-12 grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12'>
                    <ul className='grid grid-cols-2 gap-3 sm:gap-4'>
                        {businesses.items.map((item, index) => {
                            const Icon = iconMap[item.icon]
                            const active = item.id === selectedId

                            return (
                                <li key={item.id} className={cn(index === businesses.items.length - 1 && 'col-span-2 sm:col-span-1')}>
                                    <button
                                        type='button'
                                        aria-pressed={active}
                                        onClick={() => setSelectedId(item.id)}
                                        className={cn(
                                            'flex h-full min-h-14 w-full flex-col gap-3 rounded-xl border p-4 text-left transition duration-300 sm:p-5',
                                            active
                                                ? 'border-orange/60 bg-navy-700 shadow-[0_20px_50px_-30px_rgb(255_138_61/0.6)]'
                                                : 'border-soft-white/10 bg-navy-900/50 hover:-translate-y-0.5 hover:border-soft-white/25 hover:bg-navy-800'
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                'grid size-10 place-items-center rounded-lg transition',
                                                active ? 'bg-orange text-navy' : 'bg-navy-800 text-orange ring-1 ring-soft-white/10'
                                            )}
                                        >
                                            <Icon className='size-5' aria-hidden />
                                        </span>

                                        <span className='font-heading text-base font-bold text-soft-white'>
                                            {item.name}
                                        </span>

                                        <span className='hidden text-sm leading-relaxed text-cool-grey-light sm:block'>
                                            {item.explanation}
                                        </span>
                                    </button>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='lg:sticky lg:top-28 lg:self-start'>
                        <div className='mb-4 flex items-center gap-2'>
                            <StatusLabel status='concept' />
                        </div>

                        <TabletFrame>
                            <ConceptScreen
                                key={selected.id}
                                stage='engage'
                                language='en'
                                score={80}
                                speaking
                                gameIcon={SelectedIcon}
                                subtitleOverride={selected.subtitle}
                            />
                        </TabletFrame>

                        <div className='mt-6 rounded-xl border border-soft-white/10 bg-navy-900/60 p-5' aria-live='polite'>
                            <p className='text-xs font-semibold tracking-[0.14em] text-orange uppercase'>
                                Example scenario · {selected.name}
                            </p>

                            <p className='mt-3 leading-relaxed text-soft-white/90'>
                                {selected.scenario}
                            </p>

                            <p className='mt-4 inline-flex items-center gap-2 rounded-lg bg-navy-800 px-3 py-2 text-sm font-medium text-soft-white'>
                                Next step: {selected.nextStep}
                                <ArrowRight className='size-4 text-orange' aria-hidden />
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default BusinessesSection
